import React, { useState } from 'react';
import { Button, TextField, List, ListItem, ListItemText, Checkbox, CircularProgress } from '@mui/material';
import { doc, writeBatch, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

export default function RestoreMessages() {
  const [messages, setMessages] = useState([]);
  const [selectedMessages, setSelectedMessages] = useState({});
  const [loading, setLoading] = useState(false);
  const [oldUserId, setOldUserId] = useState('Ci0DceSF9Td26AkPW1m0');
  const [newUserId, setNewUserId] = useState('');
  const [startDate, setStartDate] = useState('');

  const findAndMigrateChats = async () => {
    if (!oldUserId || !newUserId) {
      alert('Veuillez renseigner les IDs utilisateur');
      return;
    }

    if (!startDate) {
      alert('Veuillez sélectionner une date de début');
      return;
    }

    setLoading(true);
    const chatsToMigrate = [];
    const startDateTime = new Date(startDate);
    startDateTime.setHours(0, 0, 0, 0);

    try {
      // 1. Trouver tous les chats contenant l'ancien ID
      const chatsRef = collection(db, 'PRIVATE_CHATS_V2');
      const chatsSnapshot = await getDocs(chatsRef);

      for (const chatDoc of chatsSnapshot.docs) {
        const chatId = chatDoc.id;
        if (chatId.includes(oldUserId)) {
          console.log('Found chat to migrate:', chatId);
          const chatData = chatDoc.data();
          
          // Récupérer les messages après la date de début
          const messagesRef = collection(db, `PRIVATE_CHATS_V2/${chatId}/MESSAGES`);
          const messagesSnapshot = await getDocs(messagesRef);
          const messages = messagesSnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .filter(message => {
              const messageDate = message.createdAt?.toDate();
              return messageDate && messageDate >= startDateTime;
            });

          // Ne pas inclure les chats sans messages après la date
          if (messages.length === 0) {
            console.log('Skipping chat, no messages after start date:', chatId);
            continue;
          }

          // Récupérer les membres
          const membersRef = collection(db, `PRIVATE_CHATS_V2/${chatId}/members`);
          const membersSnapshot = await getDocs(membersRef);
          const members = membersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          // Créer le nouveau chat ID et name
          const newChatId = chatId.replace(oldUserId, newUserId);
          const newChatName = chatData.name?.replace(oldUserId, newUserId) || '';

          chatsToMigrate.push({
            oldChatId: chatId,
            newChatId: newChatId,
            oldName: chatData.name || '',
            newName: newChatName,
            messages,
            members,
            chatData
          });
        }
      }

      setMessages(chatsToMigrate);
      console.log('Chats found:', chatsToMigrate.length);

    } catch (error) {
      console.error('Erreur lors de la recherche des chats:', error);
      alert('Erreur lors de la recherche des chats.');
    } finally {
      setLoading(false);
    }
  };

  const handleMessageToggle = (chatId) => {
    setSelectedMessages(prev => ({
      ...prev,
      [chatId]: !prev[chatId]
    }));
  };

  const handleRestore = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir migrer les conversations sélectionnées ?')) {
      return;
    }

    setLoading(true);
    const batch = writeBatch(db);
    const deleteBatch = writeBatch(db);
    let currentBatchSize = 0;
    const MAX_BATCH_SIZE = 500;
    const batches = [batch];
    const deleteBatches = [deleteBatch];

    try {
      for (const chat of messages) {
        if (selectedMessages[chat.oldChatId]) {
          // Créer le nouveau chat avec ses données
          const newChatRef = doc(db, 'PRIVATE_CHATS_V2', chat.newChatId);
          batch.set(newChatRef, {
            ...chat.chatData,
            name: chat.newName,
            _id: chat.newChatId
          });
          currentBatchSize++;
          
          // Copier les messages
          for (const message of chat.messages) {
            if (currentBatchSize >= MAX_BATCH_SIZE) {
              // Créer un nouveau batch si on atteint la limite
              const newBatch = writeBatch(db);
              batches.push(newBatch);
              currentBatchSize = 0;
              batch = newBatch;
            }

            const newMessageRef = doc(db, `PRIVATE_CHATS_V2/${chat.newChatId}/MESSAGES/${message.id}`);
            batch.set(newMessageRef, {
              ...message,
              chat: {
                ...message.chat,
                _id: chat.newChatId,
                name: chat.newName
              },
              user: {
                ...message.user,
                _id: message.user?._id === oldUserId ? newUserId : message.user?._id
              }
            });
            currentBatchSize++;
          }

          // Copier les membres
          for (const member of chat.members) {
            if (currentBatchSize >= MAX_BATCH_SIZE) {
              const newBatch = writeBatch(db);
              batches.push(newBatch);
              currentBatchSize = 0;
              batch = newBatch;
            }

            const newMemberId = member.id === oldUserId ? newUserId : member.id;
            const newMemberRef = doc(db, `PRIVATE_CHATS_V2/${chat.newChatId}/members/${newMemberId}`);
            batch.set(newMemberRef, {
              ...member,
              _id: newMemberId,
              userId: member.userId === oldUserId ? newUserId : member.userId
            });
            currentBatchSize++;
          }

          // Préparer la suppression de l'ancien chat et ses sous-collections
          // On le fera seulement après confirmation que la migration s'est bien passée
          const oldChatRef = doc(db, 'PRIVATE_CHATS_V2', chat.oldChatId);
          deleteBatch.delete(oldChatRef);

          for (const message of chat.messages) {
            if (currentBatchSize >= MAX_BATCH_SIZE) {
              const newDeleteBatch = writeBatch(db);
              deleteBatches.push(newDeleteBatch);
              currentBatchSize = 0;
              deleteBatch = newDeleteBatch;
            }
            const oldMessageRef = doc(db, `PRIVATE_CHATS_V2/${chat.oldChatId}/MESSAGES/${message.id}`);
            deleteBatch.delete(oldMessageRef);
            currentBatchSize++;
          }

          for (const member of chat.members) {
            if (currentBatchSize >= MAX_BATCH_SIZE) {
              const newDeleteBatch = writeBatch(db);
              deleteBatches.push(newDeleteBatch);
              currentBatchSize = 0;
              deleteBatch = newDeleteBatch;
            }
            const oldMemberRef = doc(db, `PRIVATE_CHATS_V2/${chat.oldChatId}/members/${member.id}`);
            deleteBatch.delete(oldMemberRef);
            currentBatchSize++;
          }
        }
      }

      // Exécuter tous les batches de création
      console.log('Création des nouvelles conversations...');
      await Promise.all(batches.map(b => b.commit()));
      
      // Demander confirmation avant de supprimer les anciennes conversations
      if (window.confirm('Les nouvelles conversations ont été créées. Voulez-vous supprimer les anciennes conversations ?')) {
        console.log('Suppression des anciennes conversations...');
        await Promise.all(deleteBatches.map(b => b.commit()));
        alert('Migration terminée avec succès ! Les anciennes conversations ont été supprimées.');
      } else {
        alert('Migration terminée avec succès ! Les anciennes conversations ont été conservées.');
      }
    } catch (error) {
      console.error('Erreur lors de la migration:', error);
      alert('Erreur lors de la migration des conversations.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <h2>Migration des Conversations Privées</h2>
      
      <div style={{ marginBottom: 20 }}>
        <TextField
          label="Ancien ID utilisateur"
          variant="outlined"
          fullWidth
          value={oldUserId}
          onChange={(e) => setOldUserId(e.target.value)}
          style={{ marginBottom: 10 }}
        />
        
        <TextField
          label="Nouvel ID utilisateur"
          variant="outlined"
          fullWidth
          value={newUserId}
          onChange={(e) => setNewUserId(e.target.value)}
          style={{ marginBottom: 10 }}
        />

        <TextField
          label="Date de début (messages après cette date)"
          type="date"
          variant="outlined"
          fullWidth
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          style={{ marginBottom: 10 }}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={findAndMigrateChats}
          disabled={loading || !oldUserId || !newUserId || !startDate}
          style={{ marginRight: 10 }}
        >
          Rechercher les conversations
        </Button>
        
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <CircularProgress size={20} />
            <span>Traitement en cours...</span>
          </div>
        )}
      </div>

      {messages.length > 0 && (
        <>
          <div style={{ marginBottom: 20 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRestore}
              disabled={loading || Object.keys(selectedMessages).length === 0}
            >
              Migrer les conversations sélectionnées
            </Button>
          </div>

          <List style={{ maxHeight: 400, overflow: 'auto' }}>
            {messages.map((chat) => (
              <ListItem key={chat.oldChatId}>
                <Checkbox
                  checked={!!selectedMessages[chat.oldChatId]}
                  onChange={() => handleMessageToggle(chat.oldChatId)}
                />
                <ListItemText
                  primary={`Chat: ${chat.oldName} → ${chat.newName}`}
                  secondary={`ID: ${chat.oldChatId} → ${chat.newChatId}
Messages: ${chat.messages.length} (après le ${new Date(startDate).toLocaleDateString()}), Membres: ${chat.members.length}`}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </div>
  );
}
